import { AssetSalesChargeRate } from "wealthmode-frontend/backend"

import { percentToRate, rateToPercent } from "@/lib/rate"

// Breakpoints travel as decimal strings and are converted within the string,
// never through Number, so a large or fractional amount comes back exact.
const PLAIN_NUMBER = /^(?:\d+\.?\d*|\.\d+)$/

// Drafts carry keys of their own because rows are removed from the middle, and
// index keys would hand each row's input state to its neighbour.
export type DraftRate = {
  key: number
  breakpoint: string
  percent: string
}

let nextKey = 0

export function newDraft(breakpoint: string): DraftRate {
  return { key: nextKey++, breakpoint, percent: "" }
}

export function toDrafts(rates: AssetSalesChargeRate[]): DraftRate[] {
  return rates.map((rate) => ({
    key: nextKey++,
    breakpoint: rate.breakpoint === null ? "" : formatBreakpoint(rate.breakpoint),
    percent: rateToPercent(rate.rate),
  }))
}

/** Parses drafts for saving, throwing at the first row that cannot be. */
export function fromDrafts(drafts: DraftRate[]): AssetSalesChargeRate[] {
  return drafts.map((draft, index) => {
    const breakpoint = parseBreakpoint(draft.breakpoint)
    if (breakpoint === null) {
      throw new Error(`Row ${index + 1} needs a breakpoint`)
    }

    const rate = percentToRate(draft.percent)
    if (rate === null) {
      throw new Error(`Row ${index + 1} needs a rate`)
    }

    return { breakpoint, rate }
  })
}

function formatBreakpoint(value: string): string {
  const [whole, fraction = ""] = value.split(".")

  const grouped = whole.replace(/\B(?=(\d{3})+$)/g, ",")
  const decimals = fraction.replace(/0+$/, "")

  return decimals === "" ? grouped : `${grouped}.${decimals}`
}

function parseBreakpoint(text: string): string | null {
  const trimmed = text.trim().replace(/^\$/, "").replace(/,/g, "")
  if (trimmed === "") {
    return null
  }

  if (!PLAIN_NUMBER.test(trimmed)) {
    throw new Error(`“${text}” is not an amount`)
  }

  return trimmed
}
